import React, { FC, useCallback, useEffect, useState } from 'react';
import Modal from 'molecules/Modal';
import {
  ModalCloseButton,
  ModalContent,
  ModalSubmitButton,
  ModalSubtitleText,
} from 'molecules/Modal/Modal.styles';
import { Box, Stack } from '@mui/material';
import { Report } from 'organisms/CloudOpsReportAdmin/types';

import axios from 'axios';
import dayjs from 'dayjs';
import { Document, Page, pdfjs } from 'react-pdf';
import loaderGif from 'assets/images/loaderGif.gif';
import { months } from 'utils/constants/common';
import {
  type PDFInvoiceData,
  generateInvoicePDFMake,
} from 'organisms/InvoicePDF/export';

interface InvoicePreviewProps {
  report: Report;
  onClose: () => void;
  onAfterClose: () => void;
}

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const InvoicePreview2: FC<InvoicePreviewProps> = ({
  report,
  onClose,
  onAfterClose,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [url, setUrl] = useState<string>();
  const [pages, setPages] = useState<number>(0);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setPages(numPages);
  };

  // const onDownload = () => {
  //   if (!url) return;

  //   const link = document.createElement('a');
  //   link.href = url; // response.request?.responseURL;
  //   link.setAttribute('download', 'invoice.pdf');
  //   document.body.appendChild(link);
  //   link.click();
  // };

  const onSubmit = useCallback(async () => {
    try {
      setSubmitting(true);
      await axios.get(
        `${process.env.REACT_APP_USER_SERVICE}/project/approve-report`,
        {
          params: {
            id: report.id,
          },
        }
      );
    } finally {
      setSubmitting(false);
    }

    onAfterClose();
  }, [report]);

  const renderSubtitle = () => (
    <Stack gap="16px">
      <ModalSubtitleText>
        Please confirm invoice creation for the {report.project.name} {month}{' '}
        period.
        <br />
        {pages ? `${pages} ${pages > 1 ? 'pages' : 'page'}` : ''}
      </ModalSubtitleText>
      {!loading && pages ? (
        // <StyledLoadingButton onClick={onDownload}>
        //   Download PDF
        // </StyledLoadingButton>
        <Stack direction="row" gap="16px" justifyContent="flex-end" mb="10px">
          <ModalCloseButton autoFocus variant="outlined" onClick={onClose}>
            Cancel
          </ModalCloseButton>
          <ModalSubmitButton
            autoFocus
            size="medium"
            loading={submitting}
            variant="contained"
            onClick={onSubmit}
          >
            Confirm
          </ModalSubmitButton>
        </Stack>
      ) : (
        ''
      )}
    </Stack>
  );

  useEffect(() => {
    fetchPdf();
  }, [report.id]);

  useEffect(() => () => URL.revokeObjectURL(url || ''), []);

  const month = report.month ? months[report.month - 1] : '';

  async function fetchPdf() {
    if (!report.id) return;

    try {
      setLoading(true);

      const { data } = await axios.get<PDFInvoiceData>(
        `${process.env.REACT_APP_REPORT_SERVICE}/invoices/preview`,
        { params: { report_id: report.id } }
      );

      const blobData = await generateInvoicePDFMake({
        ...data,
        transaction_date:
          data.qb_region === 'Europe'
            ? dayjs().subtract(1, 'month').endOf('month').format('MMM D, YYYY')
            : data.transaction_date,
      });
      setUrl(blobData);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      maxWidth="md"
      title="Invoice creation confirmation"
      subtitle={
        loading || !pages ? <Box sx={{ height: '94px' }} /> : renderSubtitle()
      }
      onClose={onClose}
      sx={{
        zIndex: 33333,
        '.MuiPaper-root': {
          width: '600px !important',
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
          '& > div:first-of-type': {
            borderRadius: '4px',
          },
        },
        '.modal-title-block': {
          background: '#FFFFFF',
        },
      }}
    >
      <ModalContent
        sx={{
          p: '0px !important',
          minHeight: '80px',
          overflowX: 'hidden',
          '.react-pdf__Page__canvas': {
            width: '600px !important',
          },
          '.react-pdf__Page.pdf-page': {
            mb: '10px',
          },
        }}
      >
        {!loading && url ? (
          // eslint-disable-next-line
          // @ts-ignore
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box
                sx={{
                  textAlign: 'center',
                  padding: '40px 0',
                  background: '#FFFFFF !important',
                }}
              >
                <img src={loaderGif} alt="loader" />
              </Box>
            }
          >
            {Array.from({ length: 3 }, (_, index) => (
              // eslint-disable-next-line
              // @ts-ignore
              <Page
                key={index + 1}
                pageNumber={index + 1}
                className="pdf-page"
              />
            ))}
          </Document>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              padding: '40px 0',
              background: '#FFFFFF !important',
            }}
          >
            <img src={loaderGif} alt="loader" />
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};

export default InvoicePreview2;
